/**
 * The greeting the wizard suggests — customer-portal review #11 (fix round D, 2026-09-25).
 *
 * The box used to hold the prototype's "Roma Pizzeria, good evening. What can I get for you?" as
 * real text, in English, for every business in every language: an owner who did not edit it shipped
 * another business's name. Now the box starts EMPTY and shows a suggestion made from the person's
 * own business name, in the language the agent greets in (the first chosen language — the runtime
 * greets in that one and switches when the caller does). Left empty, the suggestion is what the
 * agent says, and the Review step shows it word for word before anything is built.
 *
 * AMENDED 2026-09-29 (plan-009 step 2.4): the delivery suggestion is pizzabot's opening — the shop's
 * name first, then a short question ("McDonald's, γεια σας! Τι θα θέλατε;"). The runtime says the
 * plain hello for the time of day ("καλησπέρα σας" in the evening), from the language pack.
 *
 * A suggestion exists only for a language written here. For any other language there is none, and
 * the greeting must be written — never an English sentence instead (no English fallback anywhere).
 */

type GreetingKind = "delivery" | "survey" | "general";

/** Which kind of opening line suits the template: taking an order, asking a survey, or neither. */
function kindOf(agentTemplateCode: string, hasOrderBook: boolean): GreetingKind {
  if (hasOrderBook) return "delivery";
  return agentTemplateCode === "survey" ? "survey" : "general";
}

const SUGGESTION: Readonly<Record<string, Readonly<Record<GreetingKind, (named: string) => string>>>> = {
  en: {
    delivery: (named) => `${named}, hello! What can I get you?`,
    survey: (named) => `Hello, this is a short survey for ${named}. Do you have two minutes?`,
    general: (named) => `Hello, this is ${named}. How can I help?`,
  },
  el: {
    delivery: (named) => `${named}, γεια σας! Τι θα θέλατε;`,
    survey: (named) => `Γεια σας, κάνουμε μια σύντομη έρευνα για ${named}. Έχετε δύο λεπτά;`,
    general: (named) => `Γεια σας, εδώ ${named}. Πώς μπορώ να βοηθήσω;`,
  },
  // plan-026 PORTAL (design §37): European Spanish (Spain, usted) and European Portuguese (Portugal),
  // machine-drafted; the native reviewers sign them with the packs (plan-026 D4).
  es: {
    delivery: (named) => `${named}, ¡hola! ¿Qué le pongo?`,
    survey: (named) => `Hola, le llamamos para una breve encuesta de ${named}. ¿Tiene dos minutos?`,
    general: (named) => `Hola, le atiende ${named}. ¿En qué puedo ayudarle?`,
  },
  pt: {
    delivery: (named) => `${named}, olá! O que deseja?`,
    survey: (named) => `Olá, estamos a fazer um breve inquérito para ${named}. Tem dois minutos?`,
    general: (named) => `Olá, fala ${named}. Em que posso ajudar?`,
  },
};

/**
 * The suggested greeting for `languageCode`, or `null` when there is none for that language (or no
 * language is chosen yet).
 */
export function suggestedGreeting(input: {
  readonly agentTemplateCode: string;
  readonly hasOrderBook: boolean;
  readonly languageCode: string | undefined;
  readonly accountName: string;
}): string | null {
  if (input.languageCode === undefined) return null;
  const byKind = SUGGESTION[input.languageCode];
  if (byKind === undefined) return null;
  const named = input.accountName.trim();
  if (named === "") return null;
  return byKind[kindOf(input.agentTemplateCode, input.hasOrderBook)](named);
}
